import React, { useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import Peer from "simple-peer";
import styled from "styled-components";
import micmute from "../assets/micmute.svg";
import micunmute from "../assets/micunmute.svg";
import webcam from "../assets/webcam.svg";
import webcamoff from "../assets/webcamoff.svg";
import chatIcon from "../assets/chat.svg";
import { useParams } from "react-router-dom";
import { createGlobalStyle } from "styled-components";

const Wrapper = styled.div`
  height: 100vh;
  background: linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 100%);
  color: white;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  height: 70px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const Logo = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 0.5px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const InviteButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;
  padding: 30px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;
`;

const InviteLinkBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LinkText = styled.div`
  flex: 1;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  word-break: break-all;
`;

const CopyButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: scale(1.05);
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const GlobalStyle = createGlobalStyle`
  body, html, #root {
    margin: 0;
    padding: 0;
    height: 100%;
    background-color: #0f0f1e;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  }
`;

const IconButton = styled.button`
  width: 45px;
  height: 45px;
  border-radius: 12px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;  // Add this line

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const LeaveButton = styled.button`
  background: linear-gradient(135deg, #ff3e3e 0%, #d32f2f 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 62, 62, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 62, 62, 0.4);
  }
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const VideoArea = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  padding: 30px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`;

const ChatArea = styled.div`
  width: 350px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
`;

const ChatHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 600;
  font-size: 1.1rem;
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const ChatInputWrapper = styled.form`
  display: flex;
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  gap: 10px;
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 0.95rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.5);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const SendButton = styled.button`
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  }
`;

const Footer = styled.div`
  height: 90px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const ControlButton = styled.div`
  width: 55px;
  height: 55px;
  border-radius: 50%;
  background: ${(props) =>
    props.active ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 62, 62, 0.8)"};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    background: ${(props) =>
      props.active ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 62, 62, 1)"};
  }
`;

const PiP = styled.div`
  position: fixed;
  bottom: 110px;
  left: 30px;
  width: 280px;
  height: 210px;
  border: 2px solid rgba(102, 126, 234, 0.5);
  border-radius: 16px;
  overflow: hidden;
  background: #000000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 10;
`;

const UserLabel = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.8) 100%);
  color: white;
  font-size: 0.9rem;
  padding: 12px 15px;
  font-weight: 600;
  z-index: 2;
`;

const GridVideo = styled.video`
  width: 600px;
  height: 500px;
  object-fit: cover;
  border-radius: 16px;
  background-color: #000;
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const VideoWrapper = styled.div`
  position: relative;
  text-align: center;
`;

const ParticipantName = styled.div`
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  font-size: 1rem;
`;

const Icon = styled.img`
  height: 26px;
  width: 26px;
  object-fit: contain;
`;

const ControlSmall = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
`;

const ImgComponentSmall = styled.img`
  height: 16px;
  opacity: ${(props) => (props.active ? 0.9 : 0.3)};
  filter: brightness(0) invert(1);
`;

const MessageBubble = styled.div`
  display: flex;
  justify-content: ${(props) => (props.isOwn ? "flex-end" : "flex-start")};
`;

const MessageContent = styled.div`
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 16px;
  background: ${(props) =>
    props.isOwn
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "rgba(255, 255, 255, 0.1)"};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  color: white;
  text-align: left;
`;

const MessageAuthor = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
  font-size: 0.85rem;
  opacity: 0.9;
`;

const MessageText = styled.div`
  font-size: 0.95rem;
  line-height: 1.4;
`;

const Video = ({ peer }) => {
  const ref = useRef();

  useEffect(() => {
    if (!peer) return;
    const handleStream = (stream) => {
      if (ref.current) ref.current.srcObject = stream;
    };
    peer.on("stream", handleStream);
    return () => peer.removeListener("stream", handleStream);
  }, [peer]);

  return <GridVideo playsInline autoPlay ref={ref} />;
};

const Room = (props) => {
  const [peers, setPeers] = useState([]);
  const [audioFlag, setAudioFlag] = useState(true);
  const [videoFlag, setVideoFlag] = useState(true);
  const [userUpdate, setUserUpdate] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [Username, setUsername] = useState(null);
  const [CallerID, setCallerId] = useState(null);
  const [usernames, setUsernames] = useState({});
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  const socketRef = useRef();
  const userVideo = useRef();
  const peersRef = useRef([]);
  const roomID = props.match.params.roomID;

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (!username) {
      window.location.href = `/join/${roomID}`;
    } else {
      setUsername(username);
    }
  }, [roomID]);

  const videoConstraints = {
    minAspectRatio: 1.333,
    minFrameRate: 60,
    height: window.innerHeight / 1.8,
    width: window.innerWidth / 2,
  };

  useEffect(() => {
    if (!socketRef.current) return;

    socketRef.current.on("chat message", (data) => {
      console.log("Received message:", data);
      setMessages((msgs) => [...msgs, data.message]);

      if (!showChat) {
        setHasUnreadMessages(true);
      }
    });
  }, []);

  useEffect(() => {
    socketRef.current = io.connect(window.location.origin);
    createStream();
  }, []);

  function createStream() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("Camera access is not available. Please use HTTPS or localhost.");
      return;
    }
    
    navigator.mediaDevices
      .getUserMedia({ video: videoConstraints, audio: true })
      .then((stream) => {
        userVideo.current.srcObject = stream;
        const username = localStorage.getItem("username") || "Anonymous";
        socketRef.current.emit("join room", { roomID, username });

        socketRef.current.on("all users", (users) => {
          const peers = [];
          const namesMap = {};

          users.forEach((user) => {
            const userID = user.id;
            const name = user.name || "Anonymous";

            namesMap[userID] = name;

            const peer = createPeer(userID, socketRef.current.id, stream);
            peersRef.current.push({ peerID: userID, peer });
            peers.push({ peerID: userID, peer });
          });

          setUsernames(namesMap);
          setPeers(peers);
        });

        socketRef.current.on("user joined", (payload) => {
          const callerID = payload.callerID;
          const peer = addPeer(payload.signal, callerID, stream);

          peersRef.current.push({ peerID: callerID, peer });

          setPeers((users) => {
            if (users.find((p) => p.peerID === callerID)) return users;
            return [...users, { peer, peerID: callerID }];
          });

          setUsernames((prev) => ({
            ...prev,
            [callerID]: payload.username || "Anonymous",
          }));
        });

        socketRef.current.on("user left", (id) => {
          const peerObj = peersRef.current.find((p) => p.peerID === id);
          if (peerObj) peerObj.peer.destroy();
          peersRef.current = peersRef.current.filter((p) => p.peerID !== id);
          setPeers(peersRef.current);
        });

        socketRef.current.on("receiving returned signal", (payload) => {
          const item = peersRef.current.find((p) => p.peerID === payload.id);
          if (item) item.peer.signal(payload.signal);

          setUsernames((prev) => ({
            ...prev,
            [payload.id]: payload.username || "Patent",
          }));
        });

        socketRef.current.on("change", (payload) => {
          setUserUpdate(payload);
        });

        socketRef.current.on("chat message", (msg) => {
          setMessages((msgs) => [...msgs, msg]);
          if (!showChat) {
            setHasUnreadMessages(true);
          }
        });
      });
  }

  function createPeer(userToSignal, callerID, stream) {
    const peer = new Peer({
      initiator: true,
      trickle: false,
      stream,
      config: {
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      },
    });

    peer.on("signal", (signal) => {
      socketRef.current.emit("sending signal", {
        userToSignal,
        callerID,
        signal,
      });
    });

    peer.on("error", (err) => {
      console.error("Peer error (createPeer):", err);
      if (err && typeof err === "object" && "error" in err) {
        console.error(
          "RTCError message:",
          err.error?.message || JSON.stringify(err.error)
        );
      }
    });

    return peer;
  }

  function addPeer(incomingSignal, callerID, stream) {
    const peer = new Peer({
      initiator: false,
      trickle: false,
      stream,
      config: {
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      },
    });

    peer.on("signal", (signal) => {
      socketRef.current.emit("returning signal", { signal, callerID });
    });

    peer.on("error", (err) => {
      console.error("Peer error (addPeer):", err);
    });

    peer.once("connect", () => {
      console.log(`Peer connection established with ${callerID}`);
      if (callerID) {
        setCallerId(callerID);
      }
    });

    try {
      if (peer.destroyed || peer._destroying) {
        console.warn("Tried to signal a destroyed peer.");
      } else {
        peer.signal(incomingSignal);
      }
    } catch (err) {
      console.error("Signal error in addPeer:", err);
    }

    return peer;
  }

  const toggleVideo = () => {
    const stream = userVideo.current?.srcObject;
    if (!stream) return;
    stream.getVideoTracks().forEach((track) => {
      track.enabled = !track.enabled;
      setVideoFlag(track.enabled);
      socketRef.current.emit("change", [
        ...userUpdate,
        {
          id: socketRef.current.id,
          videoFlag: track.enabled,
          audioFlag,
        },
      ]);
    });
  };

  const toggleAudio = () => {
    const stream = userVideo.current?.srcObject;
    if (!stream) return;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !track.enabled;
      setAudioFlag(track.enabled);
      socketRef.current.emit("change", [
        ...userUpdate,
        {
          id: socketRef.current.id,
          videoFlag,
          audioFlag: track.enabled,
        },
      ]);
    });
  };

  const leaveCall = () => {
    window.location.href = "/";
  };

  const copyInviteLink = () => {
    const inviteLink = window.location.href;
    navigator.clipboard.writeText(inviteLink).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const sendMessage = (e) => {
    e.preventDefault();
    const trimmed = newMessage.trim();
    if (!trimmed) return;

    const messageData = {
      roomID,
      username: Username || "Guest",
      message: trimmed,
    };

    socketRef.current.emit("chat message", messageData);
    setNewMessage("");
  };

  return (
    <>
      <GlobalStyle />
      <Wrapper>
        <Header>
          <Logo>Camden Health System</Logo>
          <HeaderActions>
            <IconButton
              onClick={() => {
                setShowChat((s) => !s);
                // Clear unread messages when opening chat
                if (!showChat) {
                  setHasUnreadMessages(false);
                }
              }}
              title="Toggle Chat"
            >
              <Icon src={chatIcon} />
              {/* Add red dot indicator */}
              {hasUnreadMessages && (
                <div
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#ff3e3e",
                    borderRadius: "50%",
                  }}
                />
              )}
            </IconButton>
            <InviteButton onClick={() => setShowInviteModal(true)}>
              Invite
            </InviteButton>
            <LeaveButton onClick={leaveCall}>Leave Call</LeaveButton>
          </HeaderActions>
        </Header>
        <Main>
          <VideoArea>
            {peers.map(({ peer, peerID }) => {
              const user = userUpdate.find((u) => u.id === peerID) || {};
              const peerName = usernames[peerID] || "Guest";
              return (
                <VideoWrapper key={peerID}>
                  <Video peer={peer} />
                  <ParticipantName>{peerName}</ParticipantName>
                  <ControlSmall>
                    <StatusIndicator>
                      <ImgComponentSmall src={webcam} active={user.videoFlag} />
                      <ImgComponentSmall
                        src={micunmute}
                        active={user.audioFlag}
                      />
                    </StatusIndicator>
                  </ControlSmall>
                </VideoWrapper>
              );
            })}

            <PiP>
              <UserLabel>{Username}</UserLabel>
              <video
                muted
                ref={userVideo}
                autoPlay
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </PiP>
          </VideoArea>

          {showChat && (
            <ChatArea>
              <ChatHeader>Chat</ChatHeader>
              <ChatMessages>
                {messages.map((msg, idx) => {
                  const isOwn = msg.username === Username;
                  return (
                    <MessageBubble key={idx} isOwn={isOwn}>
                      <MessageContent isOwn={isOwn}>
                        {!isOwn && (
                          <MessageAuthor>{msg.username}</MessageAuthor>
                        )}
                        <MessageText>{msg.message}</MessageText>
                      </MessageContent>
                    </MessageBubble>
                  );
                })}
              </ChatMessages>

              <ChatInputWrapper onSubmit={sendMessage}>
                <ChatInput
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                />
                <SendButton type="submit">Send</SendButton>
              </ChatInputWrapper>
            </ChatArea>
          )}
        </Main>
        <Footer>
          <ControlButton active={videoFlag} onClick={toggleVideo}>
            <Icon src={videoFlag ? webcam : webcamoff} />
          </ControlButton>
          <ControlButton active={audioFlag} onClick={toggleAudio}>
            <Icon src={audioFlag ? micunmute : micmute} />
          </ControlButton>
        </Footer>
      </Wrapper>

      {showInviteModal && (
        <Modal onClick={() => setShowInviteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>Invite People to Meeting</ModalHeader>
            <InviteLinkBox>
              <LinkText>{window.location.href}</LinkText>
              <CopyButton onClick={copyInviteLink}>
                {copySuccess ? "Copied!" : "Copy Link"}
              </CopyButton>
            </InviteLinkBox>
            <CloseButton onClick={() => setShowInviteModal(false)}>
              Close
            </CloseButton>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default Room;
